import { Box, Stack, useColorModeValue, useBreakpointValue, Center } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import type { DealFinderProps } from "../../types/dealfinder";
import { DealFinderContent } from "./components/DealFinderContent";
import { TabTitle } from "screens/common/components/TabTitle";
import { useCustomScrollbar } from "hooks/useCustomScrollbar";
import { useConversationContext } from "screens/thread/ConversationContext";
import { ActionButtonsWrapper } from "screens/collection/views/AutocompleteSuggestion/ActionButtonsWrapper";
import { ScrollingTickers } from "screens/common/components/ScrollingTickers";
import { registerAnonymousUser } from "state/session/operations";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import { TypingIndicator } from "screens/thread/components/cells/components";
import { HEADER_HEIGHT } from "screens/landing";
import { useSidebarNavigation, useUserPreference } from "hooks";
import { useCopyValue } from "hooks/useCopies";

export const DealFinder: React.FC<DealFinderProps> = ({ initialFilter }) => {
  const bgColor = useColorModeValue("#fbfbfb", "gray.800");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollbarStyle } = useCustomScrollbar(containerRef, { width: "2px", barTransparency: 0.2 });
  const { setShouldClearInputValue } = useConversationContext();
  const filterOptions = ["EQUITY", "ETF", "MUTUAL FUNDS", "PRIVATE"];
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((state: RootState) => state.session, shallowEqual);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const dealFinderActionButtons = useCopyValue("copy_deal_finder_action_buttons") as "all" | "filter" | "quick_links" | "none";
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { fallback: "md", ssr: false });
  const showFullWidth = useUserPreference("ui_enable_view_full_width") as boolean;
  const { currentSidebarType } = useSidebarNavigation();

  useEffect(() => {
    try {
      if (!isLoggedIn && !isAuthenticating) {
        dispatch(registerAnonymousUser());
      }
    } catch (error) {
      console.error("Anonymous authentication failed:", error);
    } finally {
      setIsAuthenticating(false);
    }
  }, [dispatch, isAuthenticating, isLoggedIn]);

  // Show loading indicator while authenticating
  if (isAuthenticating) {
    return (
      <Box bg={bgColor} fontFamily="Montserrat" overflow="auto" height={"100%"} pb="2rem">
        <TabTitle title={`Deal Finder`} />
        <Center backgroundColor={bgColor} zIndex={100} width="100%" height={`calc(100vh - ${HEADER_HEIGHT - 1}px)`}>
          <TypingIndicator />
        </Center>
      </Box>
    );
  }

  return (
    <Box
      bg={bgColor}
      fontFamily="Montserrat"
      height={"100%"}
      pb="2rem"
      css={scrollbarStyle}
      width="100%"
      mx="auto"
      pl={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" && !showFullWidth ? "0" : "1rem"}
      pr={isMobile || isTablet ? ".5rem" : currentSidebarType === "hidden" && !showFullWidth ? "0" : "1rem"}>
      <TabTitle title={`Deal Finder`} />
      <Box pb={0}>
        <Stack spacing={"2rem"} alignItems="center" pt={isMobile ? "1rem" : "2rem"}>
          <Center>
            <ScrollingTickers />
          </Center>
          {dealFinderActionButtons !== "none" && (
            <ActionButtonsWrapper showButtons={dealFinderActionButtons} size="md" uniqueFilters={filterOptions} />
          )}
          <DealFinderContent onResetInput={() => setShouldClearInputValue(true)} />
        </Stack>
      </Box>
    </Box>
  );
};

export default DealFinder;
